"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = LeadGenModalButton;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _core = require("@linaria/core");
var _base = require("@site/pattern-library-utils/base.functions");
var _traffic = require("@site/pattern-library-utils/traffic");
var _merchButton = require("@site/pattern-library-core/merch-button");
var _useIdempotentImpression = _interopRequireDefault(require("@site/pattern-library-utils/hooks/useIdempotentImpression"));
var _useScriptInjector = _interopRequireWildcard(require("@site/pattern-library-utils/hooks/useScriptInjector"));
var _text = _interopRequireDefault(require("@ux/text"));
var _box = _interopRequireDefault(require("@ux/box"));
var _spinner = _interopRequireDefault(require("@ux/spinner"));
var _domains = require("@site/pattern-library-utils/domains");
var _cookies = require("@site/pattern-library-utils/cookies");
var _leadGenScript = require("@site/pattern-library-utils/lead-gen-script");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable import/no-unassigned-import */

const boxStyles = "pl-b1547u2u";
const containerStyles = "pl-c1pl8cvu";

/** *
 * LeadGenModalButton component
 * @description A button that opens a lead generation modal when clicked. It handles script injection, loading and error states.
 * @param {Object} props - The component props.
 * @param {string} props.campaignName - The name of the campaign for commerce lead gen.
 * @param {string} props.eid - The event ID for tracking.
 * @param {string} props.errorMessage - The error message to display if the script fails to load or lead gen is unavailable.
 * @param {string} props.loadingMessage - The message to display while the script is loading.
 * @param {string} props.productIntent - The product intent for the lead gen form.
 * @param {string} props.scriptUrl - The URL of the script to load for lead gen functionality.
 * @param {string} props.scriptVersion - Deprecated, use scriptUrl instead.
 * @param {string} props.source - The source of the lead gen request, used for tracking.
 * @param {string} props.type - The type of lead gen button, defaults to 'dify-lead-gen' if not provided.
 * @param {Object} nsMerchButtonProps - Additional props for the NSMerchButton component.
 * @returns {React.ReactElement} The rendered LeadGenModalButton component.
 * @public
 *
 */
function LeadGenModalButton({
  campaignName,
  eid,
  errorMessage,
  loadingMessage,
  productIntent,
  scriptUrl,
  scriptVersion,
  source,
  type,
  ...nsMerchButtonProps
}) {
  const leadGenType = type || (scriptUrl?.includes(_leadGenScript.LEAD_GEN_BTN_TYPES.COMMERCE) ? _leadGenScript.LEAD_GEN_BTN_TYPES.COMMERCE : _leadGenScript.LEAD_GEN_BTN_TYPES.MARKETING);
  const scriptId = `${leadGenType}-modal-script`;
  const scriptSrc = scriptUrl || (0, _leadGenScript.getBundleUrl)(scriptVersion, leadGenType);
  const trafficEventProps = {
    source,
    sourceApp: _leadGenScript.SOURCE_APP
  };
  (0, _useIdempotentImpression.default)(`${eid}.impression`, trafficEventProps);
  const scriptStatus = (0, _useScriptInjector.default)({
    src: scriptSrc,
    id: scriptId
  });
  const isScriptLoading = scriptStatus === _useScriptInjector.SCRIPT_STATUS.LOADING;
  const modalError = [_useScriptInjector.SCRIPT_STATUS.ERROR, _useScriptInjector.SCRIPT_STATUS.IDLE].includes(scriptStatus) || scriptStatus === _useScriptInjector.SCRIPT_STATUS.READY && !(0, _leadGenScript.isLeadGenAvailable)(leadGenType);
  const disableBtnOnError = !errorMessage && modalError;
  if (modalError) {
    (0, _traffic.logEvent)({
      eid: `${eid}.error.impression`,
      type: 'impression',
      properties: trafficEventProps
    });
  }
  const handleOpenModalClick = () => {
    (0, _traffic.logEvent)({
      eid: `${eid}.click`,
      type: 'click',
      properties: trafficEventProps
    });
    if (!(0, _leadGenScript.isValidProductIntent)(leadGenType, productIntent)) {
      (0, _traffic.logEvent)({
        eid: `${eid}.invalid-product-intent.error`,
        type: 'impression',
        properties: {
          ...trafficEventProps,
          error: `Invalid product intent: ${productIntent} for lead gen type: ${leadGenType}`
        }
      });
    }
    try {
      const leadGenKey = _leadGenScript.LEAD_GEN_BTN_WINDOW_KEYS[leadGenType];
      window[leadGenKey].load({
        ...(leadGenType === _leadGenScript.LEAD_GEN_BTN_TYPES.COMMERCE && {
          campaignName
        }),
        customerId: (0, _cookies.getCustomerId)(eid, trafficEventProps),
        market: (0, _base.getCookieValue)('market'),
        productIntent,
        rootDomain: (0, _domains.getRootDomain)(),
        source,
        sourceApp: _leadGenScript.SOURCE_APP
      });
    } catch (e) {
      (0, _traffic.logEvent)({
        eid: `${eid}.click.error`,
        type: 'impression',
        properties: trafficEventProps
      });
    }
  };
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, isScriptLoading ? /*#__PURE__*/_react.default.createElement(_box.default, {
    className: `${boxStyles} ${containerStyles}`,
    orientation: "horizontal",
    gap: "sm",
    inlineAlignChildren: "center",
    blockAlignChildren: "center"
  }, /*#__PURE__*/_react.default.createElement(_spinner.default, {
    size: 'sm',
    "data-testid": 'spinner'
  }), /*#__PURE__*/_react.default.createElement(_text.default.span, {
    as: 'paragraph',
    size: 0,
    text: loadingMessage
  })) : /*#__PURE__*/_react.default.createElement(_merchButton.NSMerchButton, (0, _extends2.default)({}, nsMerchButtonProps, {
    disabled: disableBtnOnError,
    onClick: handleOpenModalClick,
    "data-cy": `${leadGenType}-modal-open-button`
  })), modalError && !disableBtnOnError && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    emphasis: "critical",
    size: -1
  }, errorMessage));
}
LeadGenModalButton.propTypes = {
  eid: _propTypes.default.string,
  errorMessage: _propTypes.default.string,
  loadingMessage: _propTypes.default.string,
  productIntent: _propTypes.default.string,
  scriptVersion: _propTypes.default.string,
  source: _propTypes.default.string,
  campaignName: _propTypes.default.string,
  scriptUrl: _propTypes.default.string,
  type: _propTypes.default.string
};